import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlert } from '~/components/Alerts/alertsStore';
import ResponsiveDialog from '~/components/ResponsiveDialog';
import { setAlertMessage } from '~/utils/common/helpers';

import useApiKeysModal from '../../hooks/useApiKeysModal';
import useLlmApiKeys from '../../hooks/useLlmApiKeys';
import { ManageApiKeysModalProps } from '../../utils/types';
import ApiKeysListView from './ApiKeysListView';
import CreateApiKeyView from './CreateApiKeyView';
import DeleteApiKeyView from './DeleteApiKeyView';
import ManageApiKeysListSkeleton from './ManageApiKeysListSkeleton';
import SaveApiKeyView from './SaveApiKeyView';

/**
 * Manage API Keys modal. A single dialog that swaps between four views
 * (list / create / save / delete). State lives in `useApiKeysModal`; this
 * component just wires the active view to that state.
 */
const ManageApiKeysModal = ({
  open,
  onClose,
  addonId = '',
}: ManageApiKeysModalProps) => {
  const { t } = useTranslation('aiTooling', { keyPrefix: 'apiKeys.modal' });
  const generateAlert = useAlert();

  const {
    apiKeys,
    gatewayEndpoint,
    isPending: isKeysLoading,
    isError: isKeysError,
  } = useLlmApiKeys(addonId || undefined, open);

  const {
    view,
    keys,
    newKeyName,
    setNewKeyName,
    generatedKey,
    pendingDelete,
    isCreating,
    isDeleting,
    goToList,
    goToCreate,
    submitCreate,
    cancelCreate,
    startDelete,
    confirmDelete,
    cancelDelete,
  } = useApiKeysModal(open, apiKeys, {
    addonId: addonId || undefined,
  });

  useEffect(() => {
    if (!open || isKeysLoading || !isKeysError || view !== 'list') {
      return;
    }

    setAlertMessage(t('loadError'), generateAlert, {
      severity: 'error',
    });
    onClose();
  }, [open, isKeysLoading, isKeysError, view, generateAlert, onClose, t]);

  const renderView = () => {
    if (isKeysLoading && view === 'list') {
      return <ManageApiKeysListSkeleton />;
    }

    switch (view) {
      case 'list':
        return (
          <ApiKeysListView
            open={open}
            gatewayEndpoint={gatewayEndpoint}
            keys={keys}
            onCreate={goToCreate}
            onDelete={startDelete}
            onClose={onClose}
          />
        );
      case 'create':
        return (
          <CreateApiKeyView
            name={newKeyName}
            onApiKeyNameChange={setNewKeyName}
            onSubmit={submitCreate}
            cancelCreate={cancelCreate}
            onClose={onClose}
            isSubmitting={isCreating}
          />
        );
      case 'save':
        return (
          <SaveApiKeyView
            apiKey={generatedKey}
            onDone={goToList}
            onClose={onClose}
          />
        );
      case 'delete':
        return (
          <DeleteApiKeyView
            keyName={pendingDelete?.name ?? ''}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            onClose={onClose}
            isDeleting={isDeleting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      data-testid="manage-api-keys-modal"
    >
      {renderView()}
    </ResponsiveDialog>
  );
};

export default ManageApiKeysModal;
